/**
 * Privacy-safe inventory tallies sent with encrypted saves (no entry text).
 */

export interface InventoryUsageMeta {
  entry_count: number;
  complete_count?: number;
  planned_count?: number;
  amends_completed_count?: number;
  has_plan?: boolean;
  plan_word_count?: number;
  practiced_3_days?: boolean;
}

function trim(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function isStep4Complete(type: string, item: Record<string, unknown>): boolean {
  if (!item) return false;
  switch (type) {
    case 'resentments':
      return !!(
        trim(item.who) &&
        trim(item.why) &&
        trim(item.feeling) &&
        Array.isArray(item.impacts) &&
        item.impacts.length > 0 &&
        trim(item.myPart)
      );
    case 'fears':
      return !!(
        trim(item.fear) &&
        trim(item.why) &&
        Array.isArray(item.impacts) &&
        item.impacts.length > 0
      );
    case 'harms':
      return !!(
        trim(item.whoHarmed) &&
        trim(item.how) &&
        trim(item.why) &&
        Array.isArray(item.defects) &&
        item.defects.length > 0 &&
        trim(item.alternative)
      );
    default:
      return false;
  }
}

function isStep8PersonComplete(item: Record<string, unknown>): boolean {
  return !!(
    trim(item.name) &&
    trim(item.note) &&
    trim(item.timing) &&
    trim(item.form)
  );
}

function isStep10EntryComplete(item: Record<string, unknown>): boolean {
  return !!(trim(item.date) && trim(item.note));
}

function sensitiveItems(items: Record<string, unknown>[]): Record<string, unknown>[] {
  return items.filter((item) => item._type !== 'completion_status');
}

export function computeInventoryUsageMeta(
  inventoryType: string,
  items: Record<string, unknown>[]
): InventoryUsageMeta {
  const rows = sensitiveItems(items);
  const entry_count = rows.length;
  const meta: InventoryUsageMeta = { entry_count };

  if (inventoryType === 'resentments' || inventoryType === 'fears' || inventoryType === 'harms') {
    meta.complete_count = rows.filter((item) => isStep4Complete(inventoryType, item)).length;
    return meta;
  }

  if (inventoryType === 'step8-persons') {
    meta.complete_count = rows.filter((item) => isStep8PersonComplete(item)).length;
    return meta;
  }

  if (inventoryType === 'step9-persons') {
    meta.planned_count = rows.filter((item) => trim(item.amendsPlan).length > 0).length;
    meta.amends_completed_count = rows.filter((item) => item.amendsCompleted === true).length;
    return meta;
  }

  if (inventoryType === 'step10-inventories') {
    meta.complete_count = rows.filter((item) => isStep10EntryComplete(item)).length;
    return meta;
  }

  if (inventoryType === 'step11-quiet-time-plan') {
    const row = rows[0] || {};
    const plan = trim(row.plan);
    meta.entry_count = plan ? 1 : 0;
    meta.has_plan = plan.length > 0;
    meta.plan_word_count = plan ? plan.split(/\s+/).filter(Boolean).length : 0;
    meta.practiced_3_days = row.practiced3Days === true;
    return meta;
  }

  if (inventoryType === 'step12-service-plan') {
    const row = rows[0] || {};
    const plan = trim(row.plan);
    meta.entry_count = plan ? 1 : 0;
    meta.has_plan = plan.length > 0;
    meta.plan_word_count = plan ? plan.split(/\s+/).filter(Boolean).length : 0;
    return meta;
  }

  return meta;
}
